import DraftsOutlinedIcon from "@mui/icons-material/DraftsOutlined";
import { Box, CircularProgress, Menu, MenuItem } from "@mui/material";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import BellIconOff from "@/components/Icons/BellIconOff";
import BellIconOn from "@/components/Icons/BellIconOn";
import { ampli } from "@/config";
import { ActionContext } from "@/models/ActionsProvider";
import { subscribeToDriverFeeds, unsubscribeFromDriverFeeds } from "@/models/actions/clientActions";
import type { DriverRow } from "@/types/driver";

interface Props {
  anchorPosition: { top: number; left: number } | undefined;
  drivers: DriverRow[];
  workspaceId: string;
  myWorkspaceMembershipId: string;
  onClose: () => void;
}

/** Matches which items `DriverContextMenu` renders; use to avoid opening an empty menu. */
export function getDriverContextMenuVisibility(drivers: DriverRow[]) {
  let anyUnsubscribed = false;
  let anySubscribed = false;
  let anyUnread = false;
  for (const d of drivers) {
    if (d.subscribedAliasCount < d.totalAliasCount) anyUnsubscribed = true;
    if (d.subscribedAliasCount > 0) anySubscribed = true;
    if (d.unread) anyUnread = true;
  }
  return {
    showSubscribe: anyUnsubscribed,
    showUnsubscribe: anySubscribed,
    showMarkAsRead: anyUnread,
  };
}

export function driversHaveContextMenuActions(drivers: DriverRow[]): boolean {
  const { showSubscribe, showUnsubscribe, showMarkAsRead } = getDriverContextMenuVisibility(drivers);
  return showSubscribe || showUnsubscribe || showMarkAsRead;
}

function subscribeLabel(drivers: DriverRow[]): string {
  if (drivers.length > 1) {
    return `Turn on notifications for ${drivers.length} drivers`;
  }
  const driver = drivers[0];
  const name = driver.name ?? "this driver";
  const remaining = driver.totalAliasCount - driver.subscribedAliasCount;
  if (driver.totalAliasCount <= 1) {
    return `Turn on notifications for ${name}`;
  }
  if (driver.subscribedAliasCount === 0) {
    return `Turn on notifications for all ${driver.totalAliasCount} aliases for ${name}`;
  }
  return `Turn on notifications for remaining ${remaining} aliases for ${name}`;
}

function unsubscribeLabel(drivers: DriverRow[]): string {
  if (drivers.length > 1) {
    return `Turn off notifications for ${drivers.length} drivers`;
  }
  const driver = drivers[0];
  const name = driver.name ?? "this driver";
  if (driver.totalAliasCount <= 1) {
    return `Turn off notifications for ${name}`;
  }
  return `Turn off all notifications for ${name}`;
}

function markAsReadLabel(drivers: DriverRow[]): string {
  if (drivers.length > 1) {
    return `Mark ${drivers.length} drivers as read`;
  }
  const name = drivers[0].name ?? "this driver";
  return `Mark ${name} as read`;
}

export default function DriverContextMenu({
  anchorPosition,
  drivers,
  workspaceId,
  myWorkspaceMembershipId,
  onClose,
}: Props) {
  const { accountEvent } = useContext(ActionContext);
  const [loading, setLoading] = useState(false);

  const { showSubscribe, showUnsubscribe, showMarkAsRead } = useMemo(
    () => getDriverContextMenuVisibility(drivers),
    [drivers],
  );

  const hasAnyMenuItems = showSubscribe || showUnsubscribe || showMarkAsRead;

  useEffect(() => {
    if (anchorPosition != null && drivers.length > 0 && !hasAnyMenuItems) {
      onClose();
    }
  }, [anchorPosition, drivers.length, hasAnyMenuItems, onClose]);

  const handleMarkAsRead = useCallback(() => {
    if (!accountEvent) return;
    for (const driver of drivers) {
      if (driver.unread && driver.feedId != null) {
        accountEvent("Marked Feed as Read", { feedId: driver.feedId });
        if (driver.accountId) {
          ampli.markDriverAsRead({ accountGuid: driver.accountId });
        }
      }
    }
    onClose();
  }, [drivers, accountEvent, onClose]);

  const handleSubscribe = useCallback(async () => {
    setLoading(true);
    try {
      await subscribeToDriverFeeds({
        workspaceId,
        workspaceMembershipId: myWorkspaceMembershipId,
        driverWorkspaceMembershipIds: drivers.map((d) => d.workspaceMembershipId),
      });
      for (const driver of drivers) {
        if (driver.accountId) {
          ampli.activateNotifications({ accountGuid: driver.accountId });
        }
      }
    } finally {
      setLoading(false);
      onClose();
    }
  }, [drivers, workspaceId, myWorkspaceMembershipId, onClose]);

  const handleUnsubscribe = useCallback(async () => {
    setLoading(true);
    try {
      await unsubscribeFromDriverFeeds({
        workspaceId,
        workspaceMembershipId: myWorkspaceMembershipId,
        driverWorkspaceMembershipIds: drivers.map((d) => d.workspaceMembershipId),
      });
      for (const driver of drivers) {
        if (driver.accountId) {
          ampli.deactivateNotifications({ accountGuid: driver.accountId });
        }
      }
    } finally {
      setLoading(false);
      onClose();
    }
  }, [drivers, workspaceId, myWorkspaceMembershipId, onClose]);

  if (drivers.length === 0 || !hasAnyMenuItems) return null;

  return (
    <Menu
      open={anchorPosition != null}
      onClose={loading ? undefined : onClose}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition}
    >
      {showMarkAsRead && (
        <MenuItem disabled={loading} onClick={handleMarkAsRead} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Box sx={{ display: "flex", width: 30, justifyContent: "center", alignItems: "center" }}>
            <DraftsOutlinedIcon sx={{ fontSize: 18 }} />
          </Box>
          <Box>{markAsReadLabel(drivers)}</Box>
        </MenuItem>
      )}
      {showSubscribe && (
        <MenuItem disabled={loading} onClick={handleSubscribe} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Box sx={{ display: "flex", width: 30, justifyContent: "center", alignItems: "center" }}>
            {loading ? <CircularProgress size={18} /> : <BellIconOn fontSize="18" />}
          </Box>
          <Box>{subscribeLabel(drivers)}</Box>
        </MenuItem>
      )}
      {showUnsubscribe && (
        <MenuItem disabled={loading} onClick={handleUnsubscribe} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Box sx={{ display: "flex", width: 30, justifyContent: "center", alignItems: "center" }}>
            {loading ? <CircularProgress size={18} /> : <BellIconOff fontSize="18" />}
          </Box>
          <Box>{unsubscribeLabel(drivers)}</Box>
        </MenuItem>
      )}
    </Menu>
  );
}
